import React from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { IUserWalletRewardItem } from '@share/common-types';

import { RewardBookingType } from '../reward-booking-type';
import { RewardStatus } from '../reward-status';

import './style.scss';

function formatRewardDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  const m = moment(dateStr, moment.ISO_8601, true);
  return m.isValid() ? m.format('MM/DD/YYYY') : '—';
}

function formatRewardValue(
  value: number | undefined,
  account: { walletNoDecimals?: boolean } | null | undefined
): string {
  if (value == null) return '—';
  const num = account?.walletNoDecimals ? Math.floor(value) : Math.floor(value * 100) / 100;
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: account?.walletNoDecimals ? 0 : 2,
    maximumFractionDigits: account?.walletNoDecimals ? 0 : 2,
  })}`;
}

function formatRewardAmountUsed(
  value: number | undefined,
  account: { walletNoDecimals?: boolean } | null | undefined
): string {
  if (value == null) return '$0.00';
  const num = account?.walletNoDecimals ? Math.floor(value) : Math.floor(value * 100) / 100;
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: account?.walletNoDecimals ? 0 : 2,
    maximumFractionDigits: account?.walletNoDecimals ? 0 : 2,
  })}`;
}

interface IProps {
  reward: IUserWalletRewardItem;
  account?: { walletNoDecimals?: boolean } | null;
  displayBookingType?: boolean;
  displayRedeemDate?: boolean;
  displayStatus?: boolean;
}

export function RewardInformation(props: IProps) {
  const { reward, account, displayBookingType, displayRedeemDate, displayStatus } = props;
  const {
    earnedDate,
    expirationDate,
    reedemDate,
    rewardValue,
    rewardAmountUsed,
    bookingType,
    status,
  } = reward;

  return (
    <div className="reward-information">
      {displayBookingType && bookingType != null && (
        <div className="reward-information__item">
          <strong>
            <FormattedMessage id="rewards.column.bookingType" />:
          </strong>
          <label>
            <RewardBookingType type={bookingType} />
          </label>
        </div>
      )}
      <div className="reward-information__item">
        <strong>
          <FormattedMessage id="rewards.column.earnedDate" />:
        </strong>
        <label>{formatRewardDate(earnedDate)}</label>
      </div>
      {displayRedeemDate && (
        <div className="reward-information__item">
          <strong>
            <FormattedMessage id="rewards.column.redeemDate" />:
          </strong>
          <label>{formatRewardDate(reedemDate)}</label>
        </div>
      )}
      <div className="reward-information__item">
        <strong>
          <FormattedMessage id="rewards.column.expirationDate" />:
        </strong>
        <label>{formatRewardDate(expirationDate)}</label>
      </div>
      <div className="reward-information__item">
        <strong>
          <FormattedMessage id="rewards.column.rewardValue" />:
        </strong>
        <label>
          {formatRewardValue(rewardValue, account)}
          {displayRedeemDate ? (
            <span className="rewards-value-cell__redeemed">
              {formatRewardAmountUsed(rewardAmountUsed, account)} redeemed
            </span>
          ) : null}
        </label>
      </div>
      {displayStatus && status != null && (
        <div className="reward-information__item">
          <strong>
            <FormattedMessage id="rewards.column.status" />:
          </strong>
          <label>
            <RewardStatus status={status} />
          </label>
        </div>
      )}
    </div>
  );
}
